/**
 * What the shopper has attached to the search bar before sending: a photo,
 * up to three links, up to three notes.
 *
 * Pure (no React), so the rules are unit-tested under the repo's node jest.
 * The send path turns each item into an AI SDK attachment on the user
 * message: photos as today (a downscaled JPEG data URL), links and notes as
 * small text data URLs (shared/src/utils/chat-attachments.ts) that the chat
 * routes fold into framed text (lib/ai-search/fold-attachments.ts).
 */

import type { Attachment, Message } from 'ai';
import {
  isImageAttachment,
  linkToChatAttachment,
  noteToChatAttachment,
  readTextAttachments,
} from 'shared/src/utils/chat-attachments';
import { isAmazonLinkHost, normalizeLinkUrl } from 'shared/src/utils/link-url';

export type ComposerAttachment =
  | { kind: 'image'; id: string; file: File }
  | { kind: 'link'; id: string; url: string; host: string; amazon: boolean }
  | { kind: 'note'; id: string; text: string };

export type AttachmentKind = ComposerAttachment['kind'];

/** Per message. The chat routes read at most three links and three notes. */
export const ATTACHMENT_LIMITS = { image: 1, link: 3, note: 3, total: 6 } as const;

/** Matches the routes' cap (fold-attachments.ts NOTE_PROMPT_MAX_CHARS). */
export const NOTE_MAX_CHARS = 3_000;

export type AddAttachmentResult = 'added' | 'invalid' | 'duplicate' | 'limit';

let counter = 0;
export function newAttachmentId(): string {
  counter += 1;
  return `att-${Date.now().toString(36)}-${counter.toString(36)}`;
}

function countOf(items: readonly ComposerAttachment[], kind: AttachmentKind): number {
  return items.filter((item) => item.kind === kind).length;
}

/** Add a link (normalized; another shop's tags and trackers removed). */
export function withLink(
  items: readonly ComposerAttachment[],
  raw: string,
  id: string = newAttachmentId(),
): { items: ComposerAttachment[]; result: AddAttachmentResult; added?: Extract<ComposerAttachment, { kind: 'link' }> } {
  const normalized = normalizeLinkUrl(raw);
  if (!normalized) return { items: [...items], result: 'invalid' };
  if (items.some((item) => item.kind === 'link' && item.url === normalized.url)) {
    return { items: [...items], result: 'duplicate' };
  }
  if (countOf(items, 'link') >= ATTACHMENT_LIMITS.link || items.length >= ATTACHMENT_LIMITS.total) {
    return { items: [...items], result: 'limit' };
  }
  let amazon = false;
  try {
    amazon = isAmazonLinkHost(new URL(normalized.url).hostname);
  } catch {
    amazon = false;
  }
  const added = { kind: 'link' as const, id, url: normalized.url, host: normalized.host, amazon };
  return { items: [...items, added], result: 'added', added };
}

/** Add a note (trimmed, capped). */
export function withNote(
  items: readonly ComposerAttachment[],
  text: string,
  id: string = newAttachmentId(),
): { items: ComposerAttachment[]; result: AddAttachmentResult } {
  const trimmed = text.replace(/\r\n?/g, '\n').trim();
  if (!trimmed) return { items: [...items], result: 'invalid' };
  if (items.some((item) => item.kind === 'note' && item.text === trimmed.slice(0, NOTE_MAX_CHARS))) {
    return { items: [...items], result: 'duplicate' };
  }
  if (countOf(items, 'note') >= ATTACHMENT_LIMITS.note || items.length >= ATTACHMENT_LIMITS.total) {
    return { items: [...items], result: 'limit' };
  }
  return { items: [...items, { kind: 'note', id, text: trimmed.slice(0, NOTE_MAX_CHARS) }], result: 'added' };
}

/** Add a photo; a second photo replaces the first (one per message, as before). */
export function withImage(
  items: readonly ComposerAttachment[],
  file: File,
  id: string = newAttachmentId(),
): { items: ComposerAttachment[]; replaced: Extract<ComposerAttachment, { kind: 'image' }> | null } {
  const replaced = (items.find((item) => item.kind === 'image') as Extract<ComposerAttachment, { kind: 'image' }>) ?? null;
  const rest = items.filter((item) => item.kind !== 'image');
  return { items: [...rest, { kind: 'image', id, file }], replaced };
}

/**
 * The AI SDK attachments for a send. `preparedImage` resolves the photo the
 * bar started encoding when it was picked (null when it failed: the send then
 * goes without it, as before).
 */
export async function toChatAttachments(
  items: readonly ComposerAttachment[],
  preparedImage: (item: Extract<ComposerAttachment, { kind: 'image' }>) => Promise<Attachment | null>,
): Promise<Attachment[]> {
  const out: Attachment[] = [];
  for (const item of items) {
    if (item.kind === 'image') {
      const prepared = await preparedImage(item).catch(() => null);
      if (prepared) out.push(prepared);
    } else if (item.kind === 'link') {
      out.push(linkToChatAttachment(item.url));
    } else {
      out.push(noteToChatAttachment(item.text));
    }
  }
  return out;
}

export interface MessageAttachments {
  images: Attachment[];
  links: Array<{ url: string; host: string }>;
  notes: string[];
}

/** The photos, links and notes riding a sent message, for the thread. */
export function readMessageAttachments(msg: Pick<Message, 'experimental_attachments'>): MessageAttachments {
  const attachments = msg.experimental_attachments ?? [];
  const images = attachments.filter((att) => isImageAttachment(att) && Boolean(att.url));
  const { links, notes } = readTextAttachments(attachments);
  const seen = new Set<string>();
  const linkItems: MessageAttachments['links'] = [];
  for (const raw of links) {
    const normalized = normalizeLinkUrl(raw);
    if (!normalized || seen.has(normalized.url)) continue;
    seen.add(normalized.url);
    linkItems.push({ url: normalized.url, host: normalized.host });
  }
  return { images, links: linkItems, notes };
}

/** A note's chip label: its first words. */
export function noteChipLabel(text: string, max = 32): string {
  const firstLine = text.trim().split('\n').find((line) => line.trim()) ?? '';
  const flat = firstLine.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trim()}…` : flat;
}

/** The client's wrapper on a follow-up answer. */
const ANSWER_WRAPPER = /^\[User's answer[^\]]*\]:\s*/;

/**
 * One line for a user turn where the UI prints answers as text (history rows,
 * the top-menu answer summary): the words, or what was attached.
 */
export function attachmentSummaryLabel(
  msg: Pick<Message, 'content' | 'experimental_attachments'>,
  labels: { photo: string; link: string; note: string },
): string {
  const words = typeof msg.content === 'string' ? msg.content.replace(ANSWER_WRAPPER, '').trim() : '';
  if (words) return words;
  const { images, links, notes } = readMessageAttachments(msg);
  if (links.length > 0) return `${labels.link}: ${links[0].host}`;
  if (notes.length > 0) return `${labels.note}: ${noteChipLabel(notes[0], 40)}`;
  if (images.length > 0) return labels.photo;
  return '';
}

export function attachmentKinds(items: readonly ComposerAttachment[]): AttachmentKind[] {
  return items.map((item) => item.kind);
}
